/**
 * Cost Code API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  BulkImportCostCodesRequest,
  CostCode,
  CostCodeListParams,
  CostCodeListResponse,
  CreateCostCodeRequest,
  UpdateCostCodeRequest,
} from '../types/costCode';

/**
 * Cost Code API Endpoints
 * Note: Cost codes are organization-scoped, not project-scoped
 */
export const costCodeApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // COST CODE CRUD
    // ============================================================================

    // Get all cost codes for the organization
    getCostCodes: builder.query<ApiResponse<CostCodeListResponse>, CostCodeListParams | void>({
      query: params => ({
        url: '/cost-codes',
        params: params || {},
      }),
      providesTags: ['CostCode'],
    }),

    // Create cost code
    createCostCode: builder.mutation<ApiResponse<CostCode>, CreateCostCodeRequest>({
      query: data => ({
        url: '/cost-codes',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CostCode'],
    }),

    // Update cost code
    updateCostCode: builder.mutation<
      ApiResponse<CostCode>,
      { costCodeId: string; data: UpdateCostCodeRequest }
    >({
      query: ({ costCodeId, data }) => ({
        url: `/cost-codes/${costCodeId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CostCode'],
    }),

    // ============================================================================
    // BULK OPERATIONS
    // ============================================================================

    // Bulk import cost codes
    bulkImportCostCodes: builder.mutation<
      ApiResponse<{ imported: number; errors: string[] }>,
      BulkImportCostCodesRequest
    >({
      query: data => ({
        url: '/cost-codes/import',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CostCode'],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetCostCodesQuery,
  useCreateCostCodeMutation,
  useUpdateCostCodeMutation,
  // Bulk
  useBulkImportCostCodesMutation,
  // Lazy queries
  useLazyGetCostCodesQuery,
} = costCodeApi;

/**
 * Re-export types for convenience
 */
export type { CostCode, CostCodeListParams, CostCodeListResponse } from '../types/costCode';
